"use client";

import React, { useMemo, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { CustomerAccountResult } from "../services/customer-account-service";

/**
 * Cấp tài khoản app cho khách, hoặc đặt lại mật khẩu khi khách quên.
 *
 * Modal có hai bước:
 *   1. Nhập tên đăng nhập (và mật khẩu tạm nếu muốn tự đặt).
 *   2. Hiện mật khẩu tạm để nhân viên chép cho khách.
 *
 * Bước 2 KHÔNG được bỏ qua: mật khẩu chỉ về đúng một lần, trong DB chỉ còn hash.
 * Đóng cửa sổ mà chưa chép thì phải đặt lại mật khẩu mới có cái khác.
 */

interface Props {
  /** Tên khách, hiển thị trên tiêu đề và dùng để gợi ý tên đăng nhập. */
  customerName: string;
  customerPhone: string;
  /** Có sẵn khi đặt lại mật khẩu; bỏ trống khi cấp tài khoản mới. */
  existingUsername?: string;
  mode: "issue" | "reset";
  isSubmitting?: boolean;
  onClose: () => void;
  /** Trả về kết quả để hiện mật khẩu, hoặc `null` nếu gọi API thất bại. */
  onSubmit: (values: { username: string; password?: string }) => Promise<CustomerAccountResult | null>;
}

function suggestUsername(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  return digits ? `kh${digits}` : "";
}

const USERNAME_PATTERN = /^[a-z0-9._-]+$/;

const CustomerAccountModal: React.FC<Props> = ({
  customerName,
  customerPhone,
  existingUsername,
  mode,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const isReset = mode === "reset";

  const [username, setUsername] = useState(
    () => existingUsername ?? suggestUsername(customerPhone),
  );
  const [password, setPassword] = useState("");
  const [autoPassword, setAutoPassword] = useState(true);
  const [result, setResult] = useState<CustomerAccountResult | null>(null);

  const usernameError = useMemo(() => {
    if (isReset) return "";
    const value = username.trim();
    if (!value) return "Tên đăng nhập không được để trống";
    if (value.length < 4) return "Tên đăng nhập phải có ít nhất 4 ký tự";
    if (!USERNAME_PATTERN.test(value))
      return "Chỉ dùng chữ thường, số và các ký tự . _ -";
    return "";
  }, [username, isReset]);

  const passwordError = useMemo(() => {
    if (autoPassword) return "";
    if (password.length < 6) return "Mật khẩu tạm phải có ít nhất 6 ký tự";
    return "";
  }, [password, autoPassword]);

  const canSubmit = !usernameError && !passwordError && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const data = await onSubmit({
      username: username.trim().toLowerCase(),
      password: autoPassword ? undefined : password,
    });

    if (data) setResult(data);
  };

  const handleCopy = async () => {
    if (!result) return;
    const text = `Tên đăng nhập: ${result.username}\nMật khẩu: ${result.password}`;
    try {
      await navigator.clipboard.writeText(text);
      showToast({ message: "Đã chép tài khoản và mật khẩu", type: "success" });
    } catch {
      // Trình duyệt chặn clipboard (thường do trang không chạy HTTPS).
      showToast({
        message: "Không chép được, vui lòng bôi đen và chép thủ công",
        type: "error",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-lg max-h-[90vh] flex-col rounded-xl bg-white shadow-xl">
        {/* HEADER */}
        <div className="flex shrink-0 justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-700">
            {isReset ? "Đặt lại mật khẩu" : "Cấp tài khoản app"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>

        {result ? (
          // ==========================
          // BƯỚC 2 — hiện mật khẩu tạm
          // ==========================
          <div className="flex flex-1 flex-col min-h-0">
            <div className="flex-1 min-h-0 space-y-4 overflow-y-auto p-6">
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                Mật khẩu chỉ hiện <strong>một lần duy nhất</strong>. Hãy chép và gửi cho khách
                trước khi đóng cửa sổ — đóng rồi thì phải đặt lại mật khẩu mới.
              </div>

              <div className="rounded-lg border bg-slate-50 p-4">
                <div className="mb-3">
                  <div className="text-xs text-slate-500">Khách hàng</div>
                  <div className="font-medium text-slate-700">
                    {result.name} · {result.phone}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-xs text-slate-500">Tên đăng nhập</div>
                  <div className="select-all font-mono text-base font-semibold text-slate-800">
                    {result.username}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Mật khẩu tạm</div>
                  <div className="select-all font-mono text-base font-semibold text-slate-800">
                    {result.password}
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-500">
                Khách sẽ phải đổi mật khẩu ngay ở lần đăng nhập đầu tiên.
              </p>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-lg border px-4 py-2 text-slate-600 hover:bg-slate-50"
              >
                Chép tài khoản
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
              >
                Đã gửi cho khách
              </button>
            </div>
          </div>
        ) : (
          // ==========================
          // BƯỚC 1 — nhập thông tin
          // ==========================
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
            <div className="flex-1 min-h-0 space-y-5 overflow-y-auto p-6">
              <div className="rounded-lg border bg-slate-50 p-3 text-sm text-slate-600">
                Khách hàng: <strong className="text-slate-800">{customerName}</strong> ·{" "}
                {customerPhone}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600">
                  Tên đăng nhập <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isReset}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-slate-100 disabled:text-slate-500"
                  placeholder="kh0912345678"
                />
                {usernameError ? (
                  <p className="mt-1 text-xs text-red-500">{usernameError}</p>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">
                    {isReset
                      ? "Không đổi được tên đăng nhập khi đặt lại mật khẩu"
                      : "Chữ thường, số và các ký tự . _ - Khách dùng tên này để đăng nhập, không phải số điện thoại."}
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={autoPassword}
                    onChange={(e) => setAutoPassword(e.target.checked)}
                    className="rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  Hệ thống tự sinh mật khẩu tạm
                </label>

                {!autoPassword && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-600">
                      Mật khẩu tạm <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 w-full rounded-lg border px-3 py-2 text-slate-700 shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="Tối thiểu 6 ký tự"
                    />
                    {passwordError && <p className="mt-1 text-xs text-red-500">{passwordError}</p>}
                  </div>
                )}
              </div>

              {isReset && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  Đặt lại mật khẩu sẽ <strong>đăng xuất khách khỏi thiết bị đang dùng</strong> và
                  bắt khách đổi mật khẩu ở lần đăng nhập sau.
                </div>
              )}
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border px-4 py-2 text-slate-600 hover:bg-slate-50"
              >
                Huỷ
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isSubmitting ? "Đang xử lý…" : isReset ? "Đặt lại mật khẩu" : "Cấp tài khoản"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerAccountModal;
